import { defineNuxtPlugin, useAppConfig } from '#app';
import * as Sentry from '@sentry/vue';
import { withScope } from '@sentry/vue'
import { isBot } from '@/resources/helpers'

export default defineNuxtPlugin((nuxtApp) => {

  if (import.meta.env.DEV) return
  if (import.meta.env.SSR) return
  if (typeof window !== 'undefined' && window.location && window.location.pathname.endsWith('sw.js')) {
    return; // Don't init Sentry in SW files
  }
  const userAgent = navigator?.userAgent || '';
  if (isBot(userAgent)) return;

  const configRunTime = useRuntimeConfig();
  const { vueApp, $router } = nuxtApp;
  const router: any = $router

  Sentry.init({
    dsn: configRunTime.public.sentry.dsn,
    app: vueApp,
    integrations: [
      Sentry.browserTracingIntegration({ router, enableInp: true, }),
      Sentry.replayIntegration()
    ],
    // should prevent adblockers to block the request
    // tunnel: "/tunnel",
    trackComponents: true,
    tracePropagationTargets: ['localhost', 'storefront.artnight.rocks', /^\//],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      /Unable to preload CSS for/,
      /error loading dynamically imported module/,
      /Failed to fetch dynamically imported module/,
      /undefined is not an object \(evaluating ‘n.default’\)/,
      /Failed to fetch/,
      /Importing a module script failed/,
      /undefined is not an object \(evaluating ‘r.default’\)/,
      /Can't find variable: gmo/

    ],
    // The following enables exeptions to be logged to console despite logErrors being set to false (preventing them from being passed to the default Vue err handler)
    beforeSend(event, hint) {
      // Check if it is an exception, and if so, log it.
      if (event.exception) {
        console.error(`[Exeption handled by Sentry]: (${hint.originalException})`, { event, hint });
      }
      // Continue sending to Sentry
      return event;
    }
  });

  vueApp.mixin(
    Sentry.createTracingMixins({ trackComponents: true, timeout: 2000, hooks: ['activate', 'mount', 'update'] })
  );

  Sentry.attachErrorHandler(vueApp, {
    logErrors: false,
    attachProps: true,
    trackComponents: true,
    timeout: 2000,
    hooks: ['activate', 'mount', 'update']
  });

  nuxtApp.vueApp.config.errorHandler = (err, context) => {
    const error = err instanceof Error ? err : new Error(JSON.stringify(err));
    withScope((scope) => {
      scope.setExtra('context', context)
      Sentry.captureException(error)
    })
  }

  nuxtApp.hook('app:error', (err) => {
    const error = err instanceof Error ? err : new Error(JSON.stringify(err));
    Sentry.captureException(error)
  })

  return {
    provide: {
      sentrySetContext: (n, context) => Sentry.setContext(n, context),
      sentrySetUser: (user) => Sentry.setUser(user),
      sentrySetTag: (tagName, value) => Sentry.setTag(tagName, value),
      sentryAddBreadcrumb: (breadcrumb) => Sentry.addBreadcrumb(breadcrumb)
    }
  };
});
